"use client";

import { useState } from "react";
import { ethers } from "ethers";
import Form from "./Form";
import {
  FormStatePropsERC721,
  ErrorState,
} from "../common/tokenDeployer/types";
import { deployNFT } from "../common/tokenDeployer/utils";
import { toast } from "react-toastify";
import WalletInfo from "../common/metamask/support-files/contractConnectedWallet";
import BCopy from "../../components/b-copy";

import { ERC721 } from "../common/tokenDeployer/contracts/erc721/JsonURI/ERC721";
import { verifyContract } from "../common/tokenDeployer/utils/verifyContract";
export default function ERC721Deployer() {
  const [formState, setFormState] = useState<FormStatePropsERC721>({
    name: "",
    symbol: "",
    extension: "",
    uri: "",
    collectionSize: "",
  });

  const [isError, setIsError] = useState<ErrorState>({
    error: false,
    message: "",
    type: "",
  });

  const [walletAddress] = useState<string>("");

  const handleSubmit = async () => {
    const { name, symbol, extension, uri, collectionSize } = formState;

    if (!name) {
      setIsError({
        error: true,
        message: "Collection name is required.",
        type: "name",
      });
      return;
    }
    if (!symbol) {
      setIsError({
        error: true,
        message: "Collection symbol is required.",
        type: "symbol",
      });
      return;
    }
    if (!extension) {
      setIsError({
        error: true,
        message: "Metadata extension is required.",
        type: "extension",
      });
      return;
    }
    if (!uri) {
      setIsError({
        error: true,
        message: "Metadata URI is required.",
        type: "uri",
      });
      return;
    }
    if (!collectionSize) {
      setIsError({
        error: true,
        message: "Collection size is required.",
        type: "collectionSize",
      });
      return;
    }

    setIsError({ error: false, message: "", type: "" });

    let loadingToast: number | string | null = null;

    try {
      if (!window.ethereum) {
        throw new Error("MetaMask not installed");
      }

      const provider = new ethers.BrowserProvider(window.ethereum);
      const truncatedName =
        name.length > 30 ? name.substring(0, 30) + "..." : name;
      loadingToast = toast.loading(
        `Deploying ${truncatedName} NFT Collection...`
      );

      const contract = await deployNFT({
        name,
        symbol,
        extension,
        uri,
        collectionSize,
        web3Provider: provider,
      });

      const deployTx = contract.deploymentTransaction();

      if (!deployTx) {
        throw new Error("Deployment transaction not found");
      }
      await deployTx.wait();

      await verifyContract(contract.target.toString(), ERC721);

      if (loadingToast) {
        toast.dismiss(loadingToast);
      }
      toast.success(
        <div>
          <p>{name} NFT Collection deployed successfully!</p>
          <a
            href={`/tx/${deployTx.hash}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#3b82f6", textDecoration: "underline" }}
          >
            View in Explorer
          </a>
          <p>
            Contract Address: {contract.target.toString()}{" "}
            <BCopy value={contract.target.toString()} />
          </p>
        </div>
      );
    } catch (error: any) {
      if (loadingToast) {
        toast.dismiss(loadingToast);
      }

      if (
        error.code === 4001 ||
        error.code === "ACTION_REJECTED" ||
        error.message?.includes("user rejected") ||
        error.message?.includes("User rejected")
      ) {
        toast.error("Transaction cancelled by user");
        return;
      }

      const errorMessage = error.reason || error.message || "";
      const errorString = JSON.stringify(error).toLowerCase();
      const messageString = errorMessage.toLowerCase();

      if (
        error.code === -32000 ||
        error.code === -32603 ||
        messageString.includes("insufficient funds") ||
        messageString.includes("insufficient balance") ||
        messageString.includes("gas required exceeds allowance") ||
        messageString.includes("exceeds balance") ||
        messageString.includes("spendable balance") ||
        messageString.includes("insufficient balance for transfer") ||
        errorString.includes("insufficient funds") ||
        errorString.includes("insufficient balance")
      ) {
        toast.error(
          "Insufficient funds in wallet. Please add funds to your wallet to complete the deployment."
        );
        return;
      }

      if (
        error.code === -32000 &&
        (error.message?.includes("429") ||
          messageString.includes("429") ||
          errorString.includes("429"))
      ) {
        toast.error("Too many requests. Please wait a moment and try again.");
        return;
      }

      if (
        messageString.includes("network") ||
        messageString.includes("connection") ||
        messageString.includes("timeout") ||
        error.code === "NETWORK_ERROR"
      ) {
        toast.error(
          "Network error. Please check your connection and try again."
        );
        return;
      }

      const displayMessage = errorMessage || "An unknown error occurred";
      const truncatedError =
        displayMessage.length > 100
          ? displayMessage.substring(0, 100) + "..."
          : displayMessage;
      toast.error(`Deployment failed: ${truncatedError}`);
    }
  };

  return (
    <div className="td-container">
      <header className="td-header">
        <h1 className="td-title">NFT Collection Deployer</h1>
        <p className="td-subtitle">
          Easily deploy your ERC721 NFT Collection on the XDC Network.
        </p>
      </header>

      <section className="td-main-section">
        <WalletInfo />
        <Form
          isError={isError}
          formState={formState}
          setFormState={setFormState}
          walletAddress={walletAddress}
        />
        <button onClick={handleSubmit} className="td-deploy-button">
          Deploy Collection
        </button>
      </section>

      <section className="td-info-section">
        <h2 className="td-section-title">The ERC721 Contract</h2>
        <p className="td-section-text">
          This NFT deployer creates a standard ERC721 collection using
          OpenZeppelin's implementation. The deployed contract includes
          essential NFT functionality such as minting, transferring, and
          managing token URIs. The collection size will be fixed at deployment,
          and metadata can be stored on IPFS or any other decentralized storage
          solution.
        </p>
      </section>
    </div>
  );
}
